import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, Paper } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Upload as UploadIcon } from '@mui/icons-material';
import ImportHistoryTable from '../Components/ImportHistoryTable';
import ImportHistoryFilters, {
  ImportHistoryFiltersState,
} from '../Components/ImportHistoryFilters';
import { useGetImportHistoryQuery } from '../Api/invoiceImportApi';
import { InvoiceImportHistoryParams } from '../Types/invoiceImport.types';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';

/**
 * Page displaying invoice import history with filters and pagination
 * Integrates ImportHistoryTable + ImportHistoryFilters
 */
const ImportHistoryPage: React.FC = () => {
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(0); // 0-indexed for MUI, convert to 1-indexed for API
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter state
  const [filters, setFilters] = useState<ImportHistoryFiltersState>({
    status: null,
    searchTerm: '',
    importerType: '',
    fromDate: null,
    toDate: null,
  });

  // Build API query params
  const queryParams: InvoiceImportHistoryParams = {
    pageNumber: page + 1, // API expects 1-indexed
    pageSize: rowsPerPage,
    status: filters.status || undefined,
    searchTerm: filters.searchTerm || undefined,
    importerType: filters.importerType || undefined,
    fromDate: filters.fromDate ? filters.fromDate.format('YYYY-MM-DD') : undefined,
    toDate: filters.toDate ? filters.toDate.format('YYYY-MM-DD') : undefined,
  };

  // RTK Query
  const { data, isLoading, isError, error } = useGetImportHistoryQuery(queryParams);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [filters]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newSize: number) => {
    setRowsPerPage(newSize);
    setPage(0); // Reset to first page
  };

  const handleFilterChange = (newFilters: ImportHistoryFiltersState) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      status: null,
      searchTerm: '',
      importerType: '',
      fromDate: null,
      toDate: null,
    });
  };

  const handleBackToImport = () => {
    navigate('/expense-invoices/import');
  };

  const handleBackToInvoices = () => {
    navigate('/invoices');
  };

  const errorMessage = extractApiErrorMessage(error, 'Błąd ładowania historii importu');

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography variant="h5" gutterBottom>
                Historia importu faktur
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Przeglądaj historię importowanych plików z filtrami i paginacją
              </Typography>
            </Box>

            <Box display="flex" gap={1}>
              <Button startIcon={<ArrowBackIcon />} onClick={handleBackToInvoices} variant="text">
                Lista faktur
              </Button>
              <Button
                startIcon={<UploadIcon />}
                onClick={handleBackToImport}
                variant="contained"
                color="primary"
              >
                Nowy import
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Filters */}
        <ImportHistoryFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClear={handleClearFilters}
        />

        {/* Table */}
        <ImportHistoryTable
          data={data?.items || []}
          isLoading={isLoading}
          isError={isError}
          errorMessage={errorMessage}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.totalCount || 0}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
    </Container>
  );
};

export default ImportHistoryPage;
